import {
  CalendarMonth,
  FiscalMonth
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';

const DAYS_PER_WEEK = 7;
const MIN_ROWS = 5;

const dateRangeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric'
});

export const DateUtils = {
  parse(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      return new Date();
    }
    const [year, month, day] = parts;
    return new Date(year!, month! - 1, day!);
  },

  format(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  },

  normalize(dateStr: string): string {
    return this.format(this.parse(dateStr));
  },

  getDay(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    const lastDash = dateStr.lastIndexOf('-');
    return lastDash !== -1 ? String(Number(dateStr.slice(lastDash + 1))) : '';
  },

  formatDateRange(startDate: string, endDate: string): string {
    const start = this.parse(startDate);
    const end = this.parse(endDate);
    return `${dateRangeFormatter.format(start)} - ${dateRangeFormatter.format(end)}`;
  }
};

function createEmptyPadding(count: number): string[] {
  return count > 0 ? Array(count).fill('') : [];
}

function padToMinRows(arr: string[]): string[] {
  const remainder = arr.length % DAYS_PER_WEEK;
  const padded =
    remainder === 0
      ? arr
      : [...arr, ...createEmptyPadding(DAYS_PER_WEEK - remainder)];
  const minCells = MIN_ROWS * DAYS_PER_WEEK;
  return padded.length >= minCells
    ? padded
    : [...padded, ...createEmptyPadding(minCells - padded.length)];
}

function buildFiscalMonthGrid(startDate: Date, endDate: Date): string[] {
  const startPadding = createEmptyPadding(startDate.getDay());
  const dates: string[] = [];

  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(DateUtils.format(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return padToMinRows([...startPadding, ...dates]);
}

function getWeeks(days: string[]): string[][] {
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

function buildFromMonths(months: FiscalMonth[]): CalendarMonth[] {
  return months.map((fiscalMonth) => {
    const { startDate, endDate, label, weekNumber, year, quarter } =
      fiscalMonth;
    const monthStart = DateUtils.parse(startDate);
    const monthEnd = DateUtils.parse(endDate);
    const days = buildFiscalMonthGrid(monthStart, monthEnd);

    return {
      label,
      days,
      year,
      quarter,
      startDate,
      endDate,
      weeks: getWeeks(days),
      weekNumbers: getWeekNumbers(days, weekNumber),
      dateRangeLabel: DateUtils.formatDateRange(startDate, endDate)
    };
  });
}

function getWeekNumbers(days: string[], startWeekNumber: number): number[] {
  const weekNumbers: number[] = [startWeekNumber];
  const numberOfWeeks = Math.floor(days.length / 7);

  let weekNum = startWeekNumber;
  for (let i = 0; i < numberOfWeeks; i++) {
    weekNum++;
    weekNumbers.push(weekNum);
  }
  return weekNumbers;
}

export const CalendarBuilder = {
  buildFromMonths,
  getWeeks
};

function findLastTruthy(arr: string[]): string {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i]) {
      return arr[i]!;
    }
  }
  return '';
}

function generateCacheKey(days: string[]): string {
  if (days.length === 0) {
    return '';
  }
  const first = days.find(Boolean) || '';
  const last = findLastTruthy(days);
  return `${first}|${last}|${days.length}`;
}

function isFirstOfMonth(dateStr: string): boolean {
  if (!dateStr) {
    return false;
  }
  const lastDash = dateStr.lastIndexOf('-');
  return lastDash !== -1 && Number(dateStr.slice(lastDash + 1)) === 1;
}

export class MonthSpanCalculator {
  private weekMonthChangeCache = new Map<
    string,
    { hasChange: boolean; firstOfMonthIdx: number }
  >();

  isNewMonthStart(week: string[], dayIndex: number): boolean {
    return isFirstOfMonth(week[dayIndex]!);
  }

  isInNewMonth(week: string[], dayIndex: number): boolean {
    const day = week[dayIndex];
    if (!day) {
      return false;
    }

    const cacheKey = generateCacheKey(week);
    let info = this.weekMonthChangeCache.get(cacheKey);

    if (!info) {
      const firstOfMonthIdx = week.findIndex(isFirstOfMonth);
      // >= 0 so weeks starting on the 1st still get the top border
      info = { hasChange: firstOfMonthIdx >= 0, firstOfMonthIdx };
      this.weekMonthChangeCache.set(cacheKey, info);
    }

    return info.hasChange && dayIndex >= info.firstOfMonthIdx;
  }

  clearCache(): void {
    this.weekMonthChangeCache.clear();
  }
}
